import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api.ts";
import { feriadosEntre, fechaLarga, sumarDias, type AjusteFeriados } from "../../../dominio/src/index.ts";
import { AvisoError, hoy, Seccion, useAccion } from "./comun.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";

/**
 * Configuración → Horario de atención → Feriados. La agenda marca los feriados de Ecuador (se calculan con la ley: Carnaval, Viernes Santo y los traslados al lunes o viernes).
 * Como el Gobierno a veces decreta días «puente» y cada ciudad tiene sus feriados locales, aquí la clínica puede agregar días propios y quitar los que no aplican.
 */
export function FeriadosClinica() {
  const [aj, setAj] = useState<AjusteFeriados | null>(null);
  const [fecha, setFecha] = useState("");
  const [nombre, setNombre] = useState("");
  const a = useAccion();
  useEffect(() => { api<AjusteFeriados>("GET", "/api/config/feriados").then(setAj).catch(a.setError); }, []);
  const desde = hoy();
  // los nacionales de los próximos 18 meses tal como los fija la ley (sin los ajustes de la clínica)
  const nacionales = useMemo(() => feriadosEntre(desde, sumarDias(desde, 548)), [desde]);
  if (!aj) return <Seccion titulo="Feriados"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;

  const filas = [
    ...nacionales.map((f) => ({ ...f, quitado: aj.quitados.includes(f.fecha) })),
    ...aj.extras.filter((e) => e.fecha >= desde && !nacionales.some((f) => f.fecha === e.fecha)).map((e) => ({ fecha: e.fecha, nombre: e.nombre, tipo: "propio" as const, nota: undefined as string | undefined, quitado: false })),
  ].sort((x, y) => x.fecha.localeCompare(y.fecha));

  const guardar = (nuevo: AjusteFeriados) => a.correr(() => api<AjusteFeriados>("PUT", "/api/config/feriados", nuevo)).then((r) => { if (r) setAj(r); return r; });
  const quitar = (f: string) => guardar({ ...aj, quitados: [...new Set([...aj.quitados, f])] });
  const reponer = (f: string) => guardar({ ...aj, quitados: aj.quitados.filter((x) => x !== f) });
  const borrarPropio = (f: string) => guardar({ ...aj, extras: aj.extras.filter((x) => x.fecha !== f) });
  async function agregar(e: FormEvent) {
    e.preventDefault();
    if (!fecha || !nombre.trim()) return;
    const r = await guardar({ ...aj!, extras: [...aj!.extras.filter((x) => x.fecha !== fecha), { fecha, nombre: nombre.trim() }] });
    if (r) { setFecha(""); setNombre(""); }
  }

  return (
    <Seccion titulo="Feriados en la agenda">
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        La agenda marca los feriados de Ecuador en ámbar. Se calculan con la ley (por ejemplo, el 24 de mayo de 2026 cae domingo y se traslada al lunes 25) y «Llenar la agenda» no ofrece citas esos días.
        Si el Gobierno decreta un día puente o tu ciudad tiene un feriado local, agrégalo; si tu clínica atiende un feriado, quítalo. Siempre se puede agendar una cita en un feriado: solo se avisa.
      </p>
      <AvisoError error={a.error} />
      <ul className="lista-abonos">
        {filas.map((f) => (
          <li key={f.fecha} className="cumple" style={f.quitado ? { opacity: 0.6 } : undefined}>
            <div className="fila" style={{ justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap" }}>
              <span style={f.quitado ? { textDecoration: "line-through" } : undefined}><b>{fechaLarga(f.fecha)}</b> · {f.nombre} <span className="chip">{f.tipo === "propio" ? "Propio" : "Nacional"}</span>{f.quitado && <span className="chip" style={{ marginLeft: 4 }}>No aplica en mi clínica</span>}</span>
              {f.tipo === "propio" ? <button className="btn chico" disabled={a.ocupado} onClick={() => borrarPropio(f.fecha)}>Borrar</button>
                : f.quitado ? <button className="btn chico" disabled={a.ocupado} onClick={() => reponer(f.fecha)}>Volver a poner</button>
                  : <button className="btn chico" disabled={a.ocupado} onClick={() => quitar(f.fecha)} title="Mi clínica atiende ese día: deja de marcarse en la agenda">Quitar</button>}
            </div>
            {f.nota && <span className="muted" style={{ fontSize: 13 }}>{f.nota}</span>}
          </li>
        ))}
      </ul>
      <form onSubmit={agregar} className="fila" style={{ alignItems: "end", flexWrap: "wrap" }} aria-label="Agregar un día">
        <label className="campo">Día<FechaInput value={fecha} onChange={(e) => setFecha(e.target.value)} aria-label="Fecha del día" /></label>
        <label className="campo" style={{ flex: "1 1 220px" }}>Nombre (feriado local, puente o cierre)<input value={nombre} maxLength={60} onChange={(e) => setNombre(e.target.value)} placeholder="Fundación de Quito" /></label>
        <button className="btn pri" disabled={a.ocupado || !fecha || !nombre.trim()}>Agregar</button>
      </form>
    </Seccion>
  );
}
